import { useEffect, useMemo, useRef } from 'react'
import type { TranscriptSegment } from '../types'

function fmtTime(sec: number): string {
  if (!Number.isFinite(sec) || sec < 0) return '0:00'
  const m = Math.floor(sec / 60)
  const s = Math.floor(sec % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

interface TranscriptProps {
  segments: TranscriptSegment[] | null | undefined
  currentTime?: number
  onSeek?: (sec: number) => void
}

export function Transcript({ segments, currentTime = 0, onSeek }: TranscriptProps) {
  const activeIdx = useMemo(() => {
    if (!segments || !segments.length) return -1
    for (let i = 0; i < segments.length; i++) {
      const s = segments[i]
      if (currentTime >= s.start && currentTime < s.end) return i
    }
    // past the end — stick to last
    if (currentTime >= segments[segments.length - 1].end) return segments.length - 1
    return -1
  }, [segments, currentTime])

  const activeRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
  }, [])

  if (!segments || segments.length === 0) {
    return (
      <div className="rounded-md border border-slate-800 bg-slate-900/50 p-4 text-sm italic text-slate-500">
        No transcript available.
      </div>
    )
  }

  return (
    <div className="max-h-[460px] space-y-1 overflow-y-auto rounded-md border border-slate-800 bg-slate-900/50 p-3 scrollbar-thin">
      {segments.map((seg, idx) => {
        const isAgent = seg.speaker === 'agent'
        const isProspect = seg.speaker === 'prospect'
        const speakerColor = isAgent
          ? 'text-agent border-agent/60'
          : isProspect
            ? 'text-prospect border-prospect/60'
            : 'text-slate-400 border-slate-700'
        const bg = idx === activeIdx ? 'bg-slate-800/70' : 'bg-transparent'
        // Stable key: timestamp + speaker (start seconds are unique per turn).
        const key = `${seg.start.toFixed(3)}-${seg.speaker}`
        return (
          <button
            type="button"
            key={key}
            ref={idx === activeIdx ? activeRef : undefined}
            className={`w-full cursor-pointer rounded px-2 py-1.5 text-left transition-colors hover:bg-slate-800/40 ${bg}`}
            onClick={() => onSeek?.(seg.start)}
          >
            <div className="flex items-center gap-2 text-[10px]">
              <span className={`rounded border px-1.5 py-0.5 font-mono uppercase ${speakerColor}`}>
                {seg.speaker}
              </span>
              <span className="font-mono text-slate-500">
                {fmtTime(seg.start)}
              </span>
            </div>
            <p
              className={`mt-1 text-sm leading-snug ${
                isAgent ? 'text-agent' : isProspect ? 'text-prospect' : 'text-slate-300'
              }`}
            >
              {seg.text}
            </p>
          </button>
        )
      })}
    </div>
  )
}
